/**
 * Automatisk kontextuell länkning.
 *
 * Den handskrivna modellen i internalLinks.ts täcker de viktigaste
 * relationerna, men lämnar hundratals sidor utan någon väg in som inte
 * kommer från meny eller sidfot. Den här modulen fyller luckan:
 *
 *   - varje sida får ett antal kontextuella länkar UT (ring inom kluster),
 *   - ringtopologin ger därmed varje sida lika många länkar IN,
 *   - dessutom roteras länkar till affärskritiska sidor (/pulse, /priser,
 *     /film-pris-kalkyl, /case, /filmproduktion …) så de får jämn inlänkning,
 *   - och en korsklusterlänk per sida binder ihop kluster med varandra.
 *
 * Allt är deterministiskt (samma in → samma ut), så SSR och klient är
 * identiska och länkarna är stabila mellan bygg.
 */

import { isPausedPhotoPath } from "@/lib/paused-photo";
import { PAGE_CATALOG } from "@/lib/seo/link-graph.generated";
import type { InternalLink } from "@/lib/seo/internalLinks";

/** Ankare som den genererade katalogen inte lyckas formulera bra själv. */
const ANCHOR_OVERRIDES: Record<string, string> = {
  "/": "Fenny — filmproduktion för företag",
  "/priser": "Priser och paket",
  "/film-pris-kalkyl": "Räkna ut er filmbudget",
  "/pulse": "Pulse — film på abonnemang",
  "/prime": "Prime — projektproduktioner",
  "/case": "Utvalda case",
  "/kontakt": "Boka ett samtal",
  "/tjanster": "Alla tjänster",
  "/for-vem": "För vem Fenny passar",
  "/process": "Så arbetar vi",
  "/en": "Fenny — video production in Sweden",
  "/en/pricing": "Pricing and packages",
  "/en/contact": "Book a call",
};

/** Sidor som ska få jämn, kontextuell inlänkning från hela sajten. */
const PRIORITY_TARGETS_SV = [
  "/pulse",
  "/priser",
  "/film-pris-kalkyl",
  "/case",
  "/prime",
  "/tjanster",
  "/foretagsfilm",
  "/reklamfilm",
  "/employer-branding-film",
  "/for-vem",
  "/process",
];

const PRIORITY_TARGETS_EN = ["/en", "/en/pricing", "/en/contact", "/en/services"];

interface CatalogPage {
  path: string;
  anchor: string;
  cluster: string;
  isEn: boolean;
}

/**
 * Ankartexter från katalogen kapas vid maxlängd, vilket ibland lämnar en
 * hängande konjunktion/preposition sist ("… driver leads och"). Vi städar
 * bort sådana svansord så länktexten alltid läser som en avslutad fras.
 */
const DANGLING_WORDS = new Set([
  "och", "eller", "som", "för", "med", "när", "av", "till", "i", "på", "den",
  "det", "en", "ett", "från", "utan", "mellan", "är", "så", "att", "om", "vid",
  "under", "över", "men", "de", "din", "ditt", "era", "er", "ert", "vår", "vårt",
  "and", "or", "for", "with", "of", "to", "a", "an", "the", "that", "in", "on",
  "when", "your", "our",
]);

export function tidyAnchor(input: string): string {
  let words = input.trim().split(/\s+/);
  while (words.length > 2 && DANGLING_WORDS.has(words[words.length - 1].toLowerCase())) {
    words = words.slice(0, -1);
  }
  return words.join(" ").replace(/[\s,;:–—-]+$/, "");
}

const PAGES: CatalogPage[] = PAGE_CATALOG.filter(([path]) => !isPausedPhotoPath(path)).map(([path, anchor, cluster]) => ({
  path,
  anchor: ANCHOR_OVERRIDES[path] ?? tidyAnchor(anchor),
  cluster,
  isEn: cluster === "en",
}));

const BY_PATH = new Map(PAGES.map((p) => [p.path, p]));

const BY_CLUSTER = (() => {
  const map = new Map<string, CatalogPage[]>();
  for (const page of PAGES) {
    const list = map.get(page.cluster) ?? [];
    list.push(page);
    map.set(page.cluster, list);
  }
  for (const list of map.values()) list.sort((a, b) => a.path.localeCompare(b.path));
  return map;
})();

const CLUSTER_NAMES = [...BY_CLUSTER.keys()].sort();

const exists = (path: string) => BY_PATH.has(path);

const anchorFor = (path: string) => ANCHOR_OVERRIDES[path] ?? BY_PATH.get(path)?.anchor ?? path;

/** Hur många kontextuella länkar en sida minst ska ha ut. */
export const MIN_CONTEXTUAL_OUT = 8;

/** Hur många ringgrannar varje sida länkar till (= garanterad inlänkning). */
const RING_SIZE = 4;

/**
 * Genererade kontextuella länkar för en given källsida.
 * Returnerar tom lista för sidor som inte finns i katalogen (t.ex. admin).
 */
export function getGeneratedLinksFor(sourcePath: string): InternalLink[] {
  const page = BY_PATH.get(sourcePath);
  if (!page) return [];

  const siblings = BY_CLUSTER.get(page.cluster) ?? [];
  const selfIndex = Math.max(
    0,
    siblings.findIndex((p) => p.path === sourcePath),
  );

  const targets: string[] = [];
  const ring = new Set<string>();
  const push = (path?: string) => {
    if (!path || path === sourcePath || !exists(path)) return;
    if (targets.includes(path)) return;
    targets.push(path);
  };

  // 1. Ringgrannar i samma kluster — ger symmetrisk in- och utlänkning.
  for (let step = 1; step <= RING_SIZE && siblings.length > 1; step += 1) {
    const neighbour = siblings[(selfIndex + step) % siblings.length]?.path;
    push(neighbour);
    if (neighbour && neighbour !== sourcePath) ring.add(neighbour);
  }

  // 2. Affärskritiska mål, roterade så inlänkningen fördelas jämnt.
  const priority = page.isEn ? PRIORITY_TARGETS_EN : PRIORITY_TARGETS_SV;
  for (let step = 0; step < 3; step += 1) {
    push(priority[(selfIndex * 3 + step) % priority.length]);
  }

  // 3. En korsklusterlänk så kluster inte blir isolerade öar.
  const otherClusters = CLUSTER_NAMES.filter(
    (name) => name !== page.cluster && (name === "en") === page.isEn,
  );
  if (otherClusters.length > 0) {
    const cluster = otherClusters[selfIndex % otherClusters.length]!;
    const list = BY_CLUSTER.get(cluster) ?? [];
    push(list[selfIndex % Math.max(1, list.length)]?.path);
  }

  // 4. Hub/kontakt sist.
  push(page.isEn ? "/en/contact" : "/kontakt");

  return targets.map((targetPath) => ({
    sourcePath,
    targetPath,
    anchorText: anchorFor(targetPath),
    relationship: "hub" as const,
    // Ringlänkar är obligatoriska: de är grannens garanterade väg in.
    priority: ring.has(targetPath) ? ("primary" as const) : ("secondary" as const),
    language: page.isEn ? ("en" as const) : ("sv" as const),
    pageType: "knowledge" as const,
  }));
}

/** Alla katalogsidor — används av QA-script. */
export function getCatalogPaths(): string[] {
  return PAGES.map((p) => p.path);
}
